// components/Pages/RM/Inventory/InventoryTransactionModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createTransactionAction, bulkAdjustmentAction } from '../../../../utils/redux/inventoryTransaction/inventoryTransactionActions';
import { fetchAllRawMaterialAction } from '../../../../utils/redux/rawMaterial/rawMaterialActions';
import { toast } from 'react-toastify';

// Build the initial form state — pulled out so it can be reused
const buildInitialFormData = (inventoryItem) => ({
    rawMaterial: inventoryItem?.rawMaterial?._id || '',
    transactionType: 'PURCHASE',
    quantityChange: '',
    unit: inventoryItem?.currentStock?.unit || 'PCS',
    unitPrice: '',
    notes: '',
    adjustmentReason: '',
    reference: {
        type: 'PURCHASE_ORDER',
        id: '',
        number: ''
    }
});

const InventoryTransactionModal = ({ show, onHide, inventoryItem, onSuccess }) => {
    const dispatch = useDispatch();
    const { rawMaterials } = useSelector(state => state.rawMaterial);
    const { isLoading } = useSelector(state => state.inventoryTransaction);

    const [isBulk, setIsBulk] = useState(false);
    const [bulkItems, setBulkItems] = useState([]);
    const [formData, setFormData] = useState(buildInitialFormData(inventoryItem));

    // -------- RESET STATE WHENEVER MODAL OPENS OR INVENTORY ITEM CHANGES --------
    useEffect(() => {
        if (show) {
            // Fresh form each time the modal is opened
            setFormData(buildInitialFormData(inventoryItem));
            setIsBulk(false);
            setBulkItems([]);
        }
    }, [show, inventoryItem]);

    // Ensure raw materials are loaded
    useEffect(() => {
        if (!rawMaterials || rawMaterials.length === 0) {
            dispatch(fetchAllRawMaterialAction());
        }
    }, [dispatch, rawMaterials]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('reference.')) {
            const refField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                reference: {
                    ...prev.reference,
                    [refField]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleBulkChange = (index, field, value) => {
        const updatedItems = [...bulkItems];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };
        setBulkItems(updatedItems);
    };

    const addBulkItem = () => {
        setBulkItems([
            ...bulkItems,
            {
                rawMaterial: '',
                newQuantity: '',
                reason: ''
            }
        ]);
    };

    const removeBulkItem = (index) => {
        setBulkItems(bulkItems.filter((_, i) => i !== index));
    };

    const resetAndClose = () => {
        setFormData(buildInitialFormData(null));
        setIsBulk(false);
        setBulkItems([]);
        onHide();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (isBulk) {
                // Bulk adjustment
                const payload = {
                    adjustments: bulkItems.map(item => ({
                        rawMaterial: item.rawMaterial,
                        newQuantity: parseFloat(item.newQuantity),
                        reason: item.reason || formData.notes
                    })),
                    adjustmentReason: formData.adjustmentReason || 'PHYSICAL_COUNT',
                    notes: formData.notes,
                    reference: {
                        type: 'ADJUSTMENT',
                        id: formData.reference.id || new Date().getTime().toString(),
                        number: formData.reference.number || `BULK-${Date.now()}`
                    }
                };

                const response = await dispatch(bulkAdjustmentAction(payload));
                if (response?.success) {
                    toast.success('Bulk adjustment completed successfully');
                    resetAndClose();
                    if (onSuccess) onSuccess();
                } else {
                    toast.error(response?.message || 'Bulk adjustment failed');
                }
            } else {
                // Single transaction
                const payload = {
                    rawMaterial: formData.rawMaterial,
                    transactionType: formData.transactionType,
                    quantityChange: parseFloat(formData.quantityChange),
                    unit: formData.unit,
                    unitPrice: parseFloat(formData.unitPrice) || 0,
                    notes: formData.notes,
                    adjustmentReason: formData.adjustmentReason,
                    reference: {
                        type: formData.reference.type,
                        id: formData.reference.id,
                        number: formData.reference.number
                    }
                };

                // Validate
                if (!payload.rawMaterial) {
                    toast.error('Please select a raw material');
                    return;
                }
                if (!payload.quantityChange || payload.quantityChange === 0) {
                    toast.error('Please enter a valid quantity change');
                    return;
                }

                const response = await dispatch(createTransactionAction(payload));
                if (response?.success) {
                    toast.success('Transaction completed successfully');
                    resetAndClose();
                    if (onSuccess) onSuccess();
                } else {
                    toast.error(response?.message || 'Transaction failed');
                }
            }
        } catch (error) {
            toast.error(error.message || 'An error occurred');
        }
    };

    const transactionTypes = [
        { value: 'PURCHASE', label: 'Purchase' },
        { value: 'SALE', label: 'Sale' },
        { value: 'RETURN_PURCHASE', label: 'Return to Vendor' },
        { value: 'RETURN_SALE', label: 'Return from Customer' },
        { value: 'ADJUSTMENT', label: 'Adjustment' },
        { value: 'TRANSFER_IN', label: 'Transfer In' },
        { value: 'TRANSFER_OUT', label: 'Transfer Out' },
        { value: 'PRODUCTION_IN', label: 'Production In' },
        { value: 'PRODUCTION_OUT', label: 'Production Out' },
        { value: 'WASTE', label: 'Waste' },
        { value: 'SAMPLE', label: 'Sample' }
    ];

    const adjustmentReasons = [
        { value: 'PHYSICAL_COUNT', label: 'Physical Count' },
        { value: 'WASTE', label: 'Waste' },
        { value: 'DAMAGE', label: 'Damage' },
        { value: 'THEFT', label: 'Theft' },
        { value: 'RETURN', label: 'Return' },
        { value: 'CORRECTION', label: 'Correction' },
        { value: 'OTHER', label: 'Other' }
    ];

    const referenceTypes = [
        { value: 'PURCHASE_ORDER', label: 'Purchase Order' },
        { value: 'SALE_ORDER', label: 'Sale Order' },
        { value: 'PRODUCTION_ORDER', label: 'Production Order' },
        { value: 'TRANSFER_ORDER', label: 'Transfer Order' },
        { value: 'ADJUSTMENT', label: 'Adjustment' }
    ];

    const units = ['PCS', 'KG', 'METER', 'STITCH', 'HOUR', 'FT'];

    return (
        <Modal show={show} onHide={resetAndClose} size={isBulk ? 'xl' : 'lg'} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isBulk ? 'Bulk Inventory Adjustment' : 'Add Inventory Transaction'}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            label="Bulk Adjustment Mode"
                            checked={isBulk}
                            onChange={() => {
                                setIsBulk(!isBulk);
                                if (!isBulk) {
                                    setBulkItems([]);
                                }
                            }}
                        />
                    </Form.Group>

                    {!isBulk ? (
                        // Single Transaction Form
                        <>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Raw Material *</Form.Label>
                                        <Form.Select
                                            name="rawMaterial"
                                            value={formData.rawMaterial}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Raw Material</option>
                                            {rawMaterials?.map(material => (
                                                <option key={material._id} value={material._id}>
                                                    {material.name} ({material.code})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Transaction Type *</Form.Label>
                                        <Form.Select
                                            name="transactionType"
                                            value={formData.transactionType}
                                            onChange={handleChange}
                                            required
                                        >
                                            {transactionTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Quantity Change *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="quantityChange"
                                            value={formData.quantityChange}
                                            onChange={handleChange}
                                            placeholder="Positive for IN, Negative for OUT"
                                            required
                                            step="0.01"
                                        />
                                        <Form.Text className="text-muted">
                                            Positive = Stock IN, Negative = Stock OUT
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Unit *</Form.Label>
                                        <Form.Select
                                            name="unit"
                                            value={formData.unit}
                                            onChange={handleChange}
                                            required
                                        >
                                            {units.map(unit => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Unit Price</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="unitPrice"
                                            value={formData.unitPrice}
                                            onChange={handleChange}
                                            placeholder="Price per unit"
                                            step="0.01"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Adjustment Reason</Form.Label>
                                        <Form.Select
                                            name="adjustmentReason"
                                            value={formData.adjustmentReason}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Reason</option>
                                            {adjustmentReasons.map(reason => (
                                                <option key={reason.value} value={reason.value}>
                                                    {reason.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Reference Type *</Form.Label>
                                <Form.Select
                                    name="reference.type"
                                    value={formData.reference.type}
                                    onChange={handleChange}
                                    required
                                >
                                    {referenceTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Reference ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reference.id"
                                            value={formData.reference.id}
                                            onChange={handleChange}
                                            placeholder="Reference ID"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Reference Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reference.number"
                                            value={formData.reference.number}
                                            onChange={handleChange}
                                            placeholder="Reference Number"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="Additional notes..."
                                />
                            </Form.Group>
                        </>
                    ) : (
                        // Bulk Adjustment Form
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Adjustment Reason *</Form.Label>
                                <Form.Select
                                    name="adjustmentReason"
                                    value={formData.adjustmentReason}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Reason</option>
                                    {adjustmentReasons.map(reason => (
                                        <option key={reason.value} value={reason.value}>
                                            {reason.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6>Adjustment Items</h6>
                                    <Button variant="outline-primary" size="sm" onClick={addBulkItem}>
                                        + Add Item
                                    </Button>
                                </div>

                                {bulkItems.length === 0 ? (
                                    <Alert variant="info">
                                        No items added yet. Click "Add Item" to start.
                                    </Alert>
                                ) : (
                                    bulkItems.map((item, index) => (
                                        <div key={index} className="border rounded p-3 mb-2">
                                            <Row>
                                                <Col md={5}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Raw Material</Form.Label>
                                                        <Form.Select
                                                            value={item.rawMaterial}
                                                            onChange={(e) => handleBulkChange(index, 'rawMaterial', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select Material</option>
                                                            {rawMaterials?.map(material => (
                                                                <option key={material._id} value={material._id}>
                                                                    {material.name} ({material.code})
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>New Quantity</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.newQuantity}
                                                            onChange={(e) => handleBulkChange(index, 'newQuantity', e.target.value)}
                                                            placeholder="Quantity"
                                                            required
                                                            step="0.01"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Reason</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={item.reason}
                                                            onChange={(e) => handleBulkChange(index, 'reason', e.target.value)}
                                                            placeholder="Reason"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={1}>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="mt-2"
                                                        onClick={() => removeBulkItem(index)}
                                                    >
                                                        ✕
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))
                                )}
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="Additional notes for all adjustments..."
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={resetAndClose}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Processing...' : isBulk ? 'Apply Adjustments' : 'Add Transaction'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default InventoryTransactionModal;